import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";

const GOALS = [
  { id: "adelgazar", label: "Adelgazar" },
  { id: "mantener", label: "Mantener peso" },
  { id: "ganar_masa", label: "Ganar masa muscular" },
];

export default function CreateProfile() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({
    name: "",
    age: "",
    sex: "mujer",
    height_cm: "",
    weight_kg: "",
    goal: "adelgazar",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/profile", {
        ...form,
        age: parseInt(form.age, 10),
        height_cm: parseFloat(form.height_cm),
        weight_kg: parseFloat(form.weight_kg),
      });
      await refresh();
      toast.success("Perfil creado");
      navigate("/panel");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear perfil");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell
      title="Crea tu perfil"
      subtitle="Necesitamos algunos datos para personalizar tu plan y calcular tu rango fitness saludable."
      showBack={false}
    >
      <motion.form
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        className="max-w-2xl space-y-6"
      >
        <Field label="Nombre">
          <input
            data-testid="profile-name-input"
            type="text"
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className="input-mep"
          />
        </Field>

        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Edad (años)">
            <input
              data-testid="profile-age-input"
              type="number" min={10} max={120} required
              value={form.age}
              onChange={(e) => update("age", e.target.value)}
              className="input-mep"
            />
          </Field>
          <Field label="Sexo">
            <div className="grid grid-cols-2 gap-3">
              {["mujer", "hombre"].map((s) => (
                <button
                  key={s} type="button"
                  data-testid={`profile-sex-${s}`}
                  onClick={() => update("sex", s)}
                  className={`py-3 rounded-lg border capitalize transition ${
                    form.sex === s
                      ? "bg-[var(--mep-pink)] border-[var(--mep-pink)] text-white"
                      : "bg-[var(--mep-surface)] border-[var(--mep-border)] hover:border-[var(--mep-gold)]/50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Estatura (cm)">
            <input
              data-testid="profile-height-input"
              type="number" step="0.1" min={100} max={250} required
              value={form.height_cm}
              onChange={(e) => update("height_cm", e.target.value)}
              className="input-mep"
            />
          </Field>
          <Field label="Peso actual (kg)">
            <input
              data-testid="profile-weight-input"
              type="number" step="0.1" min={30} max={300} required
              value={form.weight_kg}
              onChange={(e) => update("weight_kg", e.target.value)}
              className="input-mep"
            />
          </Field>
        </div>

        <Field label="Tu objetivo">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {GOALS.map((g) => (
              <button
                key={g.id} type="button"
                data-testid={`profile-goal-${g.id}`}
                onClick={() => update("goal", g.id)}
                className={`py-3 px-4 rounded-lg border text-sm transition ${
                  form.goal === g.id
                    ? "bg-[var(--mep-pink)] border-[var(--mep-pink)] text-white"
                    : "bg-[var(--mep-surface)] border-[var(--mep-border)] hover:border-[var(--mep-gold)]/50"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </Field>

        <button
          data-testid="profile-submit-btn"
          type="submit"
          disabled={loading}
          className="btn-pink px-8 py-4 font-semibold w-full sm:w-auto"
        >
          {loading ? "Guardando..." : "Guardar y continuar"}
        </button>
      </motion.form>

      <style>{`.input-mep { width:100%; background: var(--mep-surface); border:1px solid var(--mep-border); border-radius:8px; padding: 0.75rem 1rem; color:white; outline:none; transition: border-color .2s; }
.input-mep:focus { border-color: var(--mep-pink); }`}</style>
    </AppShell>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">{label}</label>
    {children}
  </div>
);
